<template>
	<div>
		<j-card
			v-for="(item, i) in list"
			:key="i"
			class="flex sx-middle mb2 bg5 p4"
		>
			<div class="xs-6 fs3">
				<router-link
					:to="{ name: 'BindersUpdate', params: { id: item.binderId } }"
					>{{ item.binderNumber ? item.binderNumber : "---" }}</router-link
				>
			</div>
			<div class="flex xs-right xs-6">
				<crm-icon
					class="cu2"
					name="delete"
					color="cError"
					@click.native="removeContract(item.id)"
				></crm-icon>
			</div>
		</j-card>

		<!-- No data -->
		<j-card v-if="!isLoading && list && !list.length" class="bg5 p4 ta2 fs3">{{
			$t("errors.no_data")
		}}</j-card>

		<!-- Add button -->
		<div
			class="xs-12 flex xs-right mt2"
			v-if="!isLoading && list && !list.length"
		>
			<j-button size="xsmall" @click="$modal.show('addAssociatedList')">{{
				$t("add")
			}}</j-button>
		</div>

		<div class="mt6">
			<label for="commission-split" class="xs-12 fs4 fw7 lh125"
				>Commission Split</label
			>
			<commission-split
				:offer="offer"
				@input="fetchList"
				id="commission-split"
			></commission-split>
		</div>

		<!-- Add -->
		<core-modal name="addAssociatedList" :headline="$t('add')">
			<div style="min-height: 300px;">
				<div class="p4">
					<core-select-binders v-model="binders"></core-select-binders>
					<j-button
						class="mt4"
						v-if="binders && binders.length"
						:isLoading="isLoading"
						@click="addContract"
						>{{ $t("add") }}</j-button
					>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreSelectBinders from "@/modules/core/components/CoreSelectBinders.vue"
import CommissionSplit from "./AegCommissionSplit"
import CrmIcon from "@/components/CrmIcon"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JCard,
		JButton,
		CoreModal,
		CoreSelectBinders,
		CommissionSplit,
		CrmIcon
	},

	data() {
		return {
			isLoading: false,
			offer: null,
			binders: [],
			list: [],
			organizationOrInternal: null
		}
	},

	computed: {
		caseId() {
			return this.$store.state.caseManagement.caseId
		},

		proposalId() {
			return this.$store.state.caseManagement.proposalId
		},

		offerId() {
			return this.$store.state.caseManagement.offerId
		}
	},

	methods: {
		async addContract() {
			const query = gql`
				mutation addAssociatedContractToProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$associatedContractId: String!
				) {
					addAssociatedContractToProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						associatedContractId: $associatedContractId
					) {
						status
						errors
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					proposalId: this.$store.state.caseManagement.proposalId,
					offerId: this.$store.state.caseManagement.offerId,
					associatedContractId: this.binders?.[0]?.id
				}
			})
			this.isLoading = false

			if (
				res?.data?.addAssociatedContractToProposalOffer?.status !== "success"
			) {
				this.$store.dispatch(
					"showError",
					res?.data?.addAssociatedContractToProposalOffer?.errors?.[0] ||
						this.$t("errors.general")
				)
				return
			}
			this.fetchList()
			this.$modal.hide("addAssociatedList")
		},

		async fetchList() {
			const query = gql`
				query associatedContracts(
					$caseWhere: caseWhere
					$proposalWhere: proposalWhere
					$offerWhere: offerWhere
				) {
					cases(where: $caseWhere) {
						list {
							id
							proposals(where: $proposalWhere) {
								id
								basket(where: $offerWhere) {
									id
									associatedContracts {
										id
										contract {
											id
											name
											firstParty {
												id
												name
											}
											secondParty {
												id
												name
											}
											commissionRule {
												description
												jsonRule
												entities {
													id
												}
											}
											facts {
												type
												value {
													stringValue
												}
											}
										}
									}
									commissionSplitRules {
										id
										remark
										entity {
											__typename
											id
											name
										}
										baseCommissionRatio {
											ratio
										}
										currencyCode
										amount
										ratio
										type
										entity {
											name
										}
										jsonRule
									}
									commissionSummary {
										currencyCode
										amount
										ratio
										entity {
											__typename
											id
											name
										}
									}
								}
							}
						}
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					caseWhere: { id: this.$store.state.caseManagement.caseId },
					proposalWhere: { id: this.$store.state.caseManagement.proposalId },
					offerWhere: { id: this.$store.state.caseManagement.offerId }
				}
			})

			this.offer = res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]

			this.isLoading = false

			this.list =
				res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]?.associatedContracts?.map(
					item => ({
						id: item.id,
						binderId: item?.contract?.id,
						binderNumber: item?.contract?.facts?.find(
							fact => fact.type === "binderNumber"
						)?.value?.stringValue
					})
				) || []
		},

		async removeContract(associatedContractId) {
			const query = gql`
				mutation removeAssociatedContractFromProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$associatedContractId: String!
				) {
					removeAssociatedContractFromProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						associatedContractId: $associatedContractId
					) {
						status
						errors
					}
				}
			`

			const currentList = JSON.parse(JSON.stringify(this.list))
			this.list = this.list.filter(item => item.id !== associatedContractId)

			// Api Call
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					proposalId: this.$store.state.caseManagement.proposalId,
					offerId: this.$store.state.caseManagement.offerId,
					associatedContractId
				}
			})

			// Check errors
			if (
				res?.data?.removeAssociatedContractFromProposalOffer?.status !==
				"success"
			) {
				this.$store.dispatch(
					"showError",
					res?.data?.removeAssociatedContractFromProposalOffer?.errors?.[0] ||
						this.$t("errors.general")
				)
				// If error, revert optimistic UI
				this.list = currentList
				return
			}

			this.fetchList()
			this.$modal.hide("addAssociatedList")
		}
	},

	created() {
		this.fetchList()
	},

	watch: {
		"$store.state.caseManagement.offerId": {
			handler() {
				this.fetchList()
			}
		}
	}
}
</script>

<style></style>
